import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getConsolidatedDocuments } from '../api';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  FileText, Search, RefreshCw, CheckCircle, XCircle, Clock, 
  AlertTriangle, ChevronDown, ChevronUp, Users, FolderOpen,
  FileCheck, FileMinus, Download, Eye, Filter
} from 'lucide-react';
import { toast } from 'sonner';

const DocumentConsolidated = ({ user, setUser }) => {
  const [data, setData] = useState({ employees: [], summary: {}, pagination: { total: 0, page: 1, page_size: 25, total_pages: 1 } });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Debounce the search input to avoid hammering the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, pageSize]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter && statusFilter !== 'all') params.status_filter = statusFilter;
      const response = await getConsolidatedDocuments(params);
      setData(response.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch consolidated documents');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      verified: { bg: 'bg-green-100', text: 'text-green-700', icon: <CheckCircle size={12} /> },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle size={12} /> },
      pending: { bg: 'bg-amber-100', text: 'text-amber-700', icon: <Clock size={12} /> }
    };
    const c = config[status] || config.pending;
    return (
      <Badge className={`${c.bg} ${c.text} gap-1 capitalize`}>
        {c.icon}
        {status}
      </Badge>
    );
  };

  const getCompletionColor = (percentage) => {
    if (percentage >= 80) return 'bg-green-500';
    if (percentage >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  // Server-side filter handles search + status. Employees array is already the current page.
  const filteredEmployees = data.employees || [];

  // Export to CSV — exports the CURRENT page only (server-side pagination).
  // For a full export, the backend already accepts large page_size; we fetch all matching rows.
  const exportToCSV = async () => {
    try {
      toast.info('Preparing export...');
      const params = { page: 1, page_size: 500 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter && statusFilter !== 'all') params.status_filter = statusFilter;
      const res = await getConsolidatedDocuments(params);
      const all = res.data?.employees || [];
      const headers = ['Employee ID', 'Employee Name', 'Department', 'Designation', 'DOJ', 'Uploaded', 'Pending', 'Verified', 'Completion %'];
      const rows = all.map(emp => [
        emp.employee_id, emp.employee_name, emp.department, emp.designation,
        emp.date_of_joining, emp.total_uploaded, emp.total_pending, emp.verified_count, emp.completion_percentage,
      ]);
      const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell ?? ''}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `document_consolidated_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      toast.success(`Exported ${all.length} rows`);
    } catch (e) {
      console.error(e);
      toast.error('Export failed');
    }
  };

  // Check access via Permissions module (role default OR admin grant)
  const hasAccess = useSectionAccess('/documents-consolidated');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-96">
          <FolderOpen className="h-16 w-16 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
          <p className="text-slate-500 mt-2">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="document-consolidated">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Document Management - Consolidated View</h1>
            <p className="text-slate-500 text-sm">Track employee document submission and verification status</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline" className="gap-2">
              <RefreshCw size={16} />
              Refresh
            </Button>
            <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700 gap-2">
              <Download size={16} />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {data.summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Employees</p>
                    <p className="text-2xl font-bold text-slate-900">{data.summary.total_employees || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Documents Uploaded</p>
                    <p className="text-2xl font-bold text-indigo-600">{data.summary.total_documents_uploaded || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <FileMinus className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Documents Pending</p>
                    <p className="text-2xl font-bold text-amber-600">{data.summary.total_documents_pending || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <FileCheck className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Verified</p>
                    <p className="text-2xl font-bold text-green-600">{data.summary.total_verified || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <XCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Rejected</p>
                    <p className="text-2xl font-bold text-red-600">{data.summary.total_rejected || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-slate-500 mb-1 block">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by name, ID, department..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="docs-search-input"
                  />
                </div>
              </div>
              
              <div className="w-52">
                <label className="text-xs text-slate-500 mb-1 block">Status Filter</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Employees" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    <SelectItem value="complete">Complete (No Pending)</SelectItem>
                    <SelectItem value="incomplete">Incomplete (Has Pending)</SelectItem>
                    <SelectItem value="pending_verification">Pending Verification</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Employee Document List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-indigo-600" />
              Employee Documents
              <span className="text-sm font-normal text-slate-500">
                ({data.pagination?.total ?? 0} total
                {data.pagination?.total_pages > 1 && (
                  <> — page {data.pagination?.page} of {data.pagination?.total_pages}</>
                )})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : filteredEmployees.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FolderOpen className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>No employees found</p>
              </div>
            ) : (
              <div className="divide-y">
                {filteredEmployees.map((emp) => (
                  <div key={emp.id} className="hover:bg-slate-50">
                    {/* Employee Row */}
                    <div 
                      className="p-4 cursor-pointer flex items-center justify-between"
                      onClick={() => setExpandedEmployee(expandedEmployee === emp.id ? null : emp.id)}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-semibold">
                          {emp.employee_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-900">{emp.employee_name}</p>
                            <span className="text-xs text-slate-500">({emp.employee_id})</span>
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span>{emp.department || '-'}</span>
                            <span>•</span>
                            <span>{emp.designation || '-'}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Uploaded</p>
                          <p className="font-semibold text-indigo-600">{emp.total_uploaded}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Pending</p>
                          <p className={`font-semibold ${emp.total_pending > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                            {emp.total_pending}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-500">Verified</p>
                          <p className="font-semibold text-green-600">{emp.verified_count}</p>
                        </div>
                        <div className="w-32">
                          <p className="text-xs text-slate-500 mb-1">Completion</p>
                          <div className="flex items-center gap-2">
                            <Progress value={emp.completion_percentage} className="h-2" />
                            <span className="text-xs font-medium">{emp.completion_percentage}%</span>
                          </div>
                        </div>
                        {expandedEmployee === emp.id ? (
                          <ChevronUp className="text-slate-400" size={20} />
                        ) : (
                          <ChevronDown className="text-slate-400" size={20} />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedEmployee === emp.id && (
                      <div className="bg-slate-50 p-4 border-t">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Uploaded Documents */}
                          <div>
                            <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                              <FileText size={16} className="text-indigo-600" />
                              Uploaded Documents ({emp.uploaded_documents?.length || 0})
                            </h4>
                            {emp.uploaded_documents?.length > 0 ? (
                              <div className="space-y-2">
                                {emp.uploaded_documents.map((doc, idx) => (
                                  <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                                    <div>
                                      <p className="font-medium text-sm text-slate-800">{doc.document_type}</p>
                                      <p className="text-xs text-slate-500">{doc.original_filename}</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {getStatusBadge(doc.verification_status)}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 italic">No documents uploaded</p>
                            )}
                          </div>
                          
                          {/* Pending Documents */}
                          <div>
                            <h4 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
                              <AlertTriangle size={16} className="text-amber-600" />
                              Pending Documents ({emp.pending_documents?.length || 0})
                            </h4>
                            {emp.pending_documents?.length > 0 ? (
                              <div className="space-y-2">
                                {emp.pending_documents.map((doc, idx) => (
                                  <div key={idx} className="flex items-center justify-between bg-amber-50 p-3 rounded-lg border border-amber-200">
                                    <div>
                                      <p className="font-medium text-sm text-amber-800">{doc.document_type}</p>
                                      {doc.required && (
                                        <span className="text-xs text-amber-600">Required</span>
                                      )}
                                    </div>
                                    <Badge className="bg-amber-100 text-amber-700">Not Uploaded</Badge>
                                  </div>
                                ))}
                              </div>
                            ) : emp.uploaded_documents?.length > 0 ? (
                              <div className="flex items-center gap-2 text-green-600">
                                <CheckCircle size={16} />
                                <span className="text-sm">All required documents uploaded</span>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 italic">No document records found</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
          {/* Pagination Controls */}
          {(data.pagination?.total_pages ?? 1) > 1 && (
            <div className="flex items-center justify-between p-4 border-t" data-testid="docs-pagination">
              <div className="text-sm text-slate-500">
                Showing{' '}
                <span className="font-medium text-slate-700">
                  {((data.pagination.page - 1) * data.pagination.page_size) + 1}
                </span>{' '}
                –{' '}
                <span className="font-medium text-slate-700">
                  {Math.min(data.pagination.page * data.pagination.page_size, data.pagination.total)}
                </span>{' '}
                of <span className="font-medium text-slate-700">{data.pagination.total}</span>
              </div>
              <div className="flex items-center gap-2">
                <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                  <SelectTrigger className="w-28" data-testid="docs-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 / page</SelectItem>
                    <SelectItem value="25">25 / page</SelectItem>
                    <SelectItem value="50">50 / page</SelectItem>
                    <SelectItem value="100">100 / page</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={data.pagination.page <= 1 || loading}
                  data-testid="docs-prev-page"
                >
                  Previous
                </Button>
                <span className="text-sm px-2">
                  {data.pagination.page} / {data.pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(data.pagination.total_pages, p + 1))}
                  disabled={data.pagination.page >= data.pagination.total_pages || loading}
                  data-testid="docs-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-600" />
              Required Document Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.summary?.required_document_types?.map((doc, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className={doc.required ? 'border-red-200 bg-red-50 text-red-700' : 'border-slate-200'}
                >
                  {doc.name}
                  {doc.required && <span className="ml-1 text-red-500">*</span>}
                </Badge>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">* Required documents</p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DocumentConsolidated;
